import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import { eachDayOfInterval, parseISO, addDays as dfAddDays, format, isBefore } from "date-fns";
import "react-day-picker/dist/style.css";
import {
  Buildings, MapPin, Bed, Bathtub, Users, Calendar,
  CheckCircle, ArrowRight, Spinner, NavigationArrow,
  CaretLeft, CaretRight, X as XIcon, Star,
  IdentificationCard, UploadSimple, CheckFat, Clock,
} from "@phosphor-icons/react";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Delhi","Jammu and Kashmir","Ladakh",
  "Lakshadweep","Puducherry","Dadra and Nagar Haveli and Daman and Diu",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const nightsBetween = (a, b) => Math.max(Math.floor((new Date(b) - new Date(a)) / 86400000), 0);
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

export default function HotelBookingPage() {
  const { propertyId } = useParams();
  const [hotel, setHotel]       = useState(null);
  const [error, setError]       = useState(null);
  const [step, setStep]         = useState("dates"); // dates | rooms | details | review | success

  // dates
  const [range, setRange]       = useState({ from: undefined, to: undefined });
  const [guests, setGuests]     = useState(1);

  // rooms
  const [availability, setAvailability] = useState(null); // { available: [], unavailable: [] }
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // details / booking
  const [form, setForm]         = useState({ guest_name: "", guest_email: "", guest_phone: "", guest_state: "" });
  const [msg, setMsg]           = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking]   = useState(null);
  const [detectingState, setDetectingState] = useState(false);
  const [idFile, setIdFile]     = useState(null);
  const [idUrl, setIdUrl]       = useState("");
  const [uploadingId, setUploadingId] = useState(false);

  // lightbox
  const [lightbox, setLightbox] = useState(null); // { photos, idx }

  // reservation hold timer
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [holdTimeLeft, setHoldTimeLeft]   = useState(0);

  const checkIn  = range?.from ? format(range.from, "yyyy-MM-dd") : "";
  const checkOut = range?.to   ? format(range.to,   "yyyy-MM-dd") : "";
  const nights   = nightsBetween(checkIn, checkOut);

  useEffect(() => {
    fetch(`${API}/book/hotel/${propertyId}`)
      .then(r => r.ok ? r.json() : Promise.reject("Not found"))
      .then(setHotel)
      .catch(() => setError("This hotel is not available for booking."));
  }, [propertyId]);

  useEffect(() => {
    if (!holdExpiresAt) return;
    const tick = () => {
      const secs = Math.floor((new Date(holdExpiresAt) - new Date()) / 1000);
      if (secs <= 0) {
        setHoldTimeLeft(0);
        setBooking(null);
        setHoldExpiresAt(null);
        setStep("details");
        setMsg("Your 15-minute reservation hold expired. Please try again.");
      } else {
        setHoldTimeLeft(secs);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [holdExpiresAt]);

  // On-demand iCal sync when guest selects dates — closes the 15-min polling gap
  useEffect(() => {
    if (!checkIn || !checkOut) return;
    fetch(`${API}/book/hotel/${propertyId}/refresh-availability`, { method: "POST" }).catch(() => {});
  }, [checkIn, checkOut, propertyId]);

  const fetchAvailability = async () => {
    if (!checkIn || !checkOut || nights < 1) return;
    setLoadingAvail(true);
    try {
      const r = await fetch(`${API}/book/hotel/${propertyId}/availability?check_in=${checkIn}&check_out=${checkOut}`);
      const data = await r.json();
      setAvailability(data);
    } catch { setAvailability({ available: [], unavailable: [] }); }
    setLoadingAvail(false);
  };

  const handleContinueToRooms = async () => {
    if (!checkIn || !checkOut || nights < 1) { setMsg("Please select check-in and check-out dates"); return; }
    setMsg("");
    await fetchAvailability();
    setStep("rooms");
  };

  const detectState = () => {
    if (!navigator.geolocation) return;
    setDetectingState(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const data = await res.json();
          const raw = data.address?.state || "";
          const matched = INDIAN_STATES.find(s => s.toLowerCase() === raw.toLowerCase()) || raw;
          if (matched) setForm(f => ({ ...f, guest_state: matched }));
        } catch { /* silent */ }
        setDetectingState(false);
      },
      () => setDetectingState(false),
      { timeout: 8000 }
    );
  };

  const handleIdUpload = async (file) => {
    if (!file) return;
    setIdFile(file);
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/book/upload-id`, { method: "POST", body: fd });
      if (res.ok) { const data = await res.json(); setIdUrl(data.url || ""); }
    } catch { /* non-blocking */ }
    setUploadingId(false);
  };

  const handleReserve = async () => {
    // Creates 15-min hold + Aiosell OTA block, then moves to review step
    if (!form.guest_name.trim()) { setMsg("Please enter your name"); return; }
    if (!form.guest_email.trim()) { setMsg("Please enter your email"); return; }
    if (!form.guest_phone.trim()) { setMsg("Please enter your phone number"); return; }
    if (!idUrl) { setMsg("Please upload a government ID (Aadhaar / Passport / Driver's License)"); return; }
    setMsg("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/book/hotel/${propertyId}/room/${selectedRoom.room_id}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, check_in: checkIn, check_out: checkOut, guests, guest_id_url: idUrl }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Booking failed"); }
      const data = await res.json();
      setBooking(data);
      if (data.reservation_expires_at) setHoldExpiresAt(data.reservation_expires_at);
      if (data.razorpay_order_id) {
        setStep("review");
      } else {
        setStep("success");
      }
    } catch (err) {
      setMsg(err.message || "Booking failed. Please try again.");
    }
    setSubmitting(false);
  };

  const cancelHold = (bookingData) => {
    if (!bookingData?.booking_id) return;
    fetch(`${API}/book/hotel/${propertyId}/room/${bookingData.room_id || selectedRoom?.room_id}/cancel-hold`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ booking_id: bookingData.booking_id }),
    }).catch(() => {});
    setBooking(null);
    setHoldExpiresAt(null);
    setHoldTimeLeft(0);
  };

  const handlePay = async () => {
    // Hold already created in handleReserve — just open Razorpay
    if (!booking) { setMsg("Session expired. Please go back and try again."); return; }
    setMsg("");
    const loaded = await loadRazorpay();
    if (!loaded) { setMsg("Payment gateway failed to load. Please retry."); return; }
    const rp = new window.Razorpay({
      key: booking.razorpay_key_id || RAZORPAY_KEY,
      amount: booking.amount * 100,
      currency: "INR",
      name: hotel?.name || "Hotel",
      description: `${selectedRoom.name} · ${nights} night${nights !== 1 ? "s" : ""}`,
      order_id: booking.razorpay_order_id,
      prefill: { name: form.guest_name, email: form.guest_email, contact: form.guest_phone },
      handler: async (response) => {
        try {
          const cf = await fetch(`${API}/book/hotel/${propertyId}/room/${selectedRoom.room_id}/confirm-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              booking_id: booking.booking_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          if (cf.ok) { setHoldExpiresAt(null); setStep("success"); }
          else if (cf.status === 410) {
            setHoldExpiresAt(null); setBooking(null);
            setStep("details");
            setMsg("Your 15-minute reservation hold expired. Please try again.");
          } else setMsg("Payment received but confirmation failed. Please contact the host.");
        } catch { setMsg("Payment received but confirmation failed. Please contact the host."); }
      },
      modal: { ondismiss: () => { cancelHold(booking); setMsg("Payment cancelled — your hold has been released. You can book again."); } },
      theme: { color: "#E8B931" },
    });
    rp.open();
  };

  const nightly = selectedRoom?.nightly_rate || 0;
  const subtotal = nightly * nights;
  const gstRate  = nightly > 7500 ? 0.18 : nightly >= 1000 ? 0.05 : 0;
  const gstType  = form.guest_state && hotel?.state
    ? (form.guest_state.toLowerCase() === (hotel.state || "").toLowerCase() ? "intrastate" : "interstate")
    : "";
  const gstAmount  = gstType && gstRate > 0 ? Math.round(subtotal * gstRate) : 0;
  const grandTotal = subtotal + gstAmount;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Buildings size={48} className="text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  if (!hotel) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Spinner size={32} className="animate-spin text-amber-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><XIcon size={28} /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2" onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx - 1 + l.photos.length) % l.photos.length })); }}>
            <CaretLeft size={28} />
          </button>
          <img src={lightbox.photos[lightbox.idx]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl" onClick={e => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2" onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx + 1) % l.photos.length })); }}>
            <CaretRight size={28} />
          </button>
          <p className="absolute bottom-6 text-white/60 text-sm">{lightbox.idx + 1} / {lightbox.photos.length}</p>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Buildings size={22} className="text-amber-500" weight="duotone" />
          <div>
            <p className="font-semibold text-slate-900 text-sm leading-tight">{hotel.name}</p>
            <p className="text-xs text-slate-500">{hotel.city}{hotel.state ? `, ${hotel.state}` : ""}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Hotel Hero photos */}
        {hotel.photos?.length > 0 && (
          <div className="grid grid-cols-3 gap-2 rounded-2xl overflow-hidden h-48 md:h-64">
            <div className="col-span-2 row-span-2 relative cursor-pointer" onClick={() => setLightbox({ photos: hotel.photos, idx: 0 })}>
              <img src={hotel.photos[0]} alt={hotel.name} className="w-full h-full object-cover" />
            </div>
            {hotel.photos.slice(1, 3).map((url, i) => (
              <div key={i} className="relative cursor-pointer" onClick={() => setLightbox({ photos: hotel.photos, idx: i + 1 })}>
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 1 && hotel.photos.length > 3 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm font-semibold">
                    +{hotel.photos.length - 3} more
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs font-medium">
          {["dates", "rooms", "details", "review"].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step === s || (step === "success" && i < 4) ? "bg-amber-400 text-white" :
                ["dates","rooms","details","review","success"].indexOf(step) > i ? "bg-emerald-500 text-white" :
                "bg-slate-200 text-slate-500"
              }`}>{i + 1}</span>
              <span className={step === s ? "text-amber-600 font-semibold" : "text-slate-400 capitalize"}>{s}</span>
              {i < 3 && <ArrowRight size={12} className="text-slate-300" />}
            </div>
          ))}
        </div>

        {/* ── STEP: DATES ── */}
        {step === "dates" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Calendar size={16} className="text-amber-500" /> Select Dates</p>
              <DayPicker
                mode="range"
                selected={range}
                onSelect={setRange}
                disabled={[{ before: new Date() }]}
                numberOfMonths={1}
                className="!font-sans"
                styles={{ day: { borderRadius: "8px" } }}
              />
              {msg && <p className="text-red-500 text-xs mt-2">{msg}</p>}
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-3">
                <p className="text-sm font-semibold text-slate-700">Booking Summary</p>
                <div className="flex gap-3">
                  <div className="flex-1 bg-slate-50 rounded-xl p-3 border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Check-in</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{checkIn ? fmtDate(checkIn) : "—"}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">after {hotel.checkin_time || "14:00"}</p>
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-xl p-3 border border-slate-200">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Check-out</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{checkOut ? fmtDate(checkOut) : "—"}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">before {hotel.checkout_time || "11:00"}</p>
                  </div>
                </div>
                {nights > 0 && (
                  <p className="text-xs text-slate-500 text-center">{nights} night{nights !== 1 ? "s" : ""}</p>
                )}
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium text-slate-600">Guests</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setGuests(g => Math.max(1, g - 1))} className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm transition flex items-center justify-center">−</button>
                    <span className="text-sm font-semibold text-slate-800 w-4 text-center">{guests}</span>
                    <button onClick={() => setGuests(g => g + 1)} className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm transition flex items-center justify-center">+</button>
                  </div>
                </div>
                <button
                  onClick={handleContinueToRooms}
                  disabled={!checkIn || !checkOut || nights < 1}
                  className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loadingAvail ? <Spinner size={16} className="animate-spin" /> : <>See Available Rooms <ArrowRight size={16} /></>}
                </button>
              </div>
              {hotel.amenities?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Hotel Amenities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {hotel.amenities.map(a => (
                      <span key={a} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP: ROOMS ── */}
        {step === "rooms" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-800">Choose a Room</h2>
                <p className="text-xs text-slate-500 mt-0.5">{fmtDate(checkIn)} → {fmtDate(checkOut)} · {nights} night{nights !== 1 ? "s" : ""} · {guests} guest{guests !== 1 ? "s" : ""}</p>
              </div>
              <button onClick={() => { setStep("dates"); setAvailability(null); setSelectedRoom(null); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2">Change dates</button>
            </div>

            {hotel.rooms.length === 0 ? (
              <div className="text-center py-12 text-slate-400">No rooms configured for this hotel yet.</div>
            ) : (
              <div className="space-y-4">
                {hotel.rooms.map(room => {
                  const isAvailable = !availability || availability.available.includes(room.room_id);
                  return (
                    <div key={room.room_id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${isAvailable ? "border-slate-200 cursor-pointer hover:border-amber-300 hover:shadow-md" : "border-slate-200 opacity-60"}`}>
                      <div className="flex gap-0 md:gap-4">
                        {/* Room photo */}
                        <div className="w-32 md:w-48 shrink-0 relative">
                          {room.photos?.[0] ? (
                            <img
                              src={room.photos[0]}
                              alt={room.name}
                              className="w-full h-full object-cover"
                              style={{ minHeight: "120px", maxHeight: "180px" }}
                              onClick={() => isAvailable && room.photos.length > 0 && setLightbox({ photos: room.photos, idx: 0 })}
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center" style={{ minHeight: "120px" }}>
                              <Bed size={32} className="text-slate-300" />
                            </div>
                          )}
                          {!isAvailable && (
                            <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                              <span className="bg-white/90 text-slate-600 text-[10px] font-bold px-2 py-1 rounded-full">Unavailable</span>
                            </div>
                          )}
                        </div>
                        {/* Room info */}
                        <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-semibold text-slate-900 text-base">{room.name}</h3>
                              {isAvailable && (
                                <span className="shrink-0 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Available</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><Bed size={12} /> {room.bedrooms || 1} BR</span>
                              <span className="flex items-center gap-1"><Bathtub size={12} /> {room.bathrooms || 1} BA</span>
                              <span className="flex items-center gap-1"><Users size={12} /> up to {room.max_guests || 2}</span>
                            </div>
                            {room.description && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{room.description}</p>}
                          </div>
                          <div className="flex items-end justify-between mt-3 gap-3">
                            <div>
                              <p className="text-lg font-bold text-slate-900">{fmtINR(room.nightly_rate)}<span className="text-xs font-normal text-slate-400">/night</span></p>
                              {nights > 0 && <p className="text-xs text-slate-400">{fmtINR(room.nightly_rate * nights)} total for {nights} night{nights !== 1 ? "s" : ""}</p>}
                            </div>
                            {isAvailable && (
                              <button
                                onClick={() => { setSelectedRoom(room); setStep("details"); }}
                                className="shrink-0 px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold transition"
                              >
                                Select
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── STEP: DETAILS ── */}
        {step === "details" && selectedRoom && (
          <div className="grid md:grid-cols-5 gap-6">
            {/* Form */}
            <div className="md:col-span-3 space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={() => setStep("rooms")} className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
                  <CaretLeft size={12} /> Back to rooms
                </button>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
                <p className="text-sm font-semibold text-slate-700">Your Details</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Full Name *</label>
                    <input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-amber-400"
                      placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Email *</label>
                    <input type="email" value={form.guest_email} onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-amber-400"
                      placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Phone *</label>
                    <input type="tel" value={form.guest_phone} onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-amber-400"
                      placeholder="+91 9876543210" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 flex items-center justify-between">
                      Your State <span className="font-normal text-slate-400">(for GST)</span>
                      <button onClick={detectState} disabled={detectingState} className="text-[10px] font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1 disabled:opacity-50">
                        {detectingState ? <Spinner size={10} className="animate-spin" /> : <NavigationArrow size={10} />} Auto-detect
                      </button>
                    </label>
                    <select value={form.guest_state} onChange={e => setForm(f => ({ ...f, guest_state: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-amber-400 appearance-none">
                      <option value="">Select state</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <IdentificationCard size={14} className="text-slate-400" />
                      <label className="text-xs font-semibold text-slate-600">
                        Government ID * <span className="font-normal text-slate-400">(Aadhaar / Passport / Driver's License)</span>
                      </label>
                    </div>
                    <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                      idFile ? "border-green-300 bg-green-50" : "border-slate-200 hover:border-amber-300 bg-slate-50"
                    }`}>
                      <input type="file" accept="image/*,.pdf" className="hidden"
                        onChange={e => handleIdUpload(e.target.files?.[0])} />
                      {uploadingId ? (
                        <><Spinner size={16} className="animate-spin text-amber-500" /><span className="text-xs text-slate-500">Uploading…</span></>
                      ) : idFile ? (
                        <><CheckFat size={16} weight="fill" className="text-green-500 shrink-0" /><span className="text-xs text-green-700 truncate">{idFile.name}</span></>
                      ) : (
                        <><UploadSimple size={16} className="text-slate-400 shrink-0" /><span className="text-xs text-slate-400">Upload a photo or PDF of your ID</span></>
                      )}
                    </label>
                  </div>
                </div>
                {msg && <p className="text-red-500 text-xs">{msg}</p>}
                <button
                  onClick={handleReserve}
                  disabled={submitting}
                  className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Spinner size={16} className="animate-spin" /> Holding dates…</> : "Review & Hold Dates"}
                </button>
                <p className="text-[10px] text-slate-400 text-center">Secure payment via Razorpay. Your details are safe.</p>
              </div>
            </div>

            {/* Booking summary sidebar */}
            <div className="md:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-3 sticky top-20">
                <p className="text-sm font-semibold text-slate-700">Booking Summary</p>
                {selectedRoom.photos?.[0] && (
                  <img src={selectedRoom.photos[0]} alt={selectedRoom.name} className="w-full h-32 object-cover rounded-xl" />
                )}
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{selectedRoom.name}</p>
                  <p className="text-xs text-slate-400">{hotel.name}</p>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Check-in</span><span className="font-medium text-slate-800">{fmtDate(checkIn)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Check-out</span><span className="font-medium text-slate-800">{fmtDate(checkOut)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Guests</span><span className="font-medium text-slate-800">{guests}</span>
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-2 space-y-1.5 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>{fmtINR(nightly)} × {nights} night{nights !== 1 ? "s" : ""}</span>
                    <span>{fmtINR(subtotal)}</span>
                  </div>
                  {gstAmount > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>GST ({Math.round(gstRate * 100)}% {gstType})</span>
                      <span>{fmtINR(gstAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-slate-900 text-sm pt-1 border-t border-slate-100">
                    <span>Total</span><span>{fmtINR(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP: REVIEW ── */}
        {step === "review" && booking && (
          <div className="max-w-md mx-auto space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h2 className="font-semibold text-slate-800 text-base">Review Your Booking</h2>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Room</span><span className="font-semibold">{selectedRoom?.name}</span></div>
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Hotel</span><span className="font-semibold">{hotel.name}</span></div>
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Check-in</span><span className="font-semibold">{checkIn}</span></div>
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Check-out</span><span className="font-semibold">{checkOut}</span></div>
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Nights</span><span className="font-semibold">{nights}</span></div>
                <div className="flex justify-between text-slate-700"><span className="text-slate-500">Guests</span><span className="font-semibold">{guests}</span></div>
                {gstAmount > 0 && (
                  <div className="flex justify-between text-slate-500 text-xs"><span>GST ({Math.round(gstRate * 100)}%)</span><span>{fmtINR(gstAmount)}</span></div>
                )}
                <div className="flex justify-between font-bold text-slate-800 border-t border-amber-100 pt-2 text-base">
                  <span>Total</span><span className="text-amber-600">{fmtINR(grandTotal)}</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 space-y-1 text-sm">
                <p className="text-xs font-semibold text-slate-400 mb-1">YOUR DETAILS</p>
                <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-medium text-slate-800">{form.guest_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium text-slate-800">{form.guest_email}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-medium text-slate-800">{form.guest_phone}</span></div>
              </div>
              {holdExpiresAt && holdTimeLeft > 0 && (
                <div className={`flex items-center gap-2 text-xs rounded-xl px-3 py-2 ${
                  holdTimeLeft <= 120
                    ? "bg-red-50 border border-red-100 text-red-600"
                    : "bg-amber-50 border border-amber-100 text-amber-700"
                }`}>
                  <Clock size={13} weight="bold" />
                  <span>
                    Dates held for&nbsp;
                    <strong>{Math.floor(holdTimeLeft / 60)}:{String(holdTimeLeft % 60).padStart(2, "0")}</strong>
                    &nbsp;— complete payment before the timer expires
                  </span>
                </div>
              )}
              {msg && <p className="text-red-500 text-xs">{msg}</p>}
              <button onClick={handlePay}
                className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm transition flex items-center justify-center gap-2">
                Confirm & Pay {fmtINR(grandTotal)}
              </button>
              <button onClick={() => { cancelHold(booking); setStep("details"); }}
                className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 transition">
                ← Back to edit details
              </button>
            </div>
            <p className="text-center text-xs text-slate-400">Dates are held for 15 minutes from when you proceed to review.</p>
          </div>
        )}

        {/* ── STEP: SUCCESS ── */}
        {step === "success" && (
          <div className="max-w-md mx-auto text-center py-12 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle size={36} className="text-emerald-500" weight="fill" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Booking Confirmed!</h2>
            <p className="text-slate-500 text-sm">
              Your booking for <strong>{selectedRoom?.name}</strong> at <strong>{hotel.name}</strong> is confirmed.
              A confirmation has been sent to <strong>{form.guest_email}</strong>.
            </p>
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-left space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Check-in</span><span className="font-medium">{fmtDate(checkIn)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Check-out</span><span className="font-medium">{fmtDate(checkOut)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Room</span><span className="font-medium">{selectedRoom?.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Guests</span><span className="font-medium">{guests}</span></div>
              <div className="flex justify-between font-semibold"><span>Total Paid</span><span>{fmtINR(grandTotal)}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
